/**
 * GreenHealthRecovery — pure SoR4-style special-cost HP regen state machine.
 *
 * When a hero activates a special, HP is deducted and that amount becomes
 * "green" (recoverable). If the hero lands GREEN_RECOVERY_HIT_THRESHOLD hits
 * within GREEN_RECOVERY_WINDOW_FRAMES, the green HP is restored in full.
 * Taking damage during the window locks it in as permanent loss.
 *
 * No Phaser import — unit-testable in node.
 */

/** HP deducted from the player when a special is activated. */
export const SPECIAL_HP_COST = 20;

/**
 * HP the super should actually deduct given the hero's current HP.
 * Clamped to never reduce the hero below 1 HP, so a meter-funded super can never
 * self-KO (which would waste the star already spent and cost a life). At full HP
 * this is just `fullCost`; only the low-HP desperation case costs less.
 */
export function superSelfDamageCost(currentHp: number, fullCost = SPECIAL_HP_COST): number {
  return Math.min(fullCost, Math.max(0, Math.floor(currentHp) - 1));
}

/** Recovery window duration in frames (3 seconds at 60 fps). */
export const GREEN_RECOVERY_WINDOW_FRAMES = 180;

/** Hits the hero must land within the window to restore green HP. */
export const GREEN_RECOVERY_HIT_THRESHOLD = 2;

export interface GreenHealthState {
  /** HP currently "green" (recoverable if threshold met). 0 = no active window. */
  greenAmount: number;
  /** Frames remaining in the recovery window. 0 = window closed. */
  framesLeft: number;
  /** Hits landed by the hero since the window opened. */
  hitsLanded: number;
}

export function createGreenHealthState(): GreenHealthState {
  return { greenAmount: 0, framesLeft: 0, hitsLanded: 0 };
}

/** True while a recovery window is active and green HP can still be reclaimed. */
export function hasActiveGreenWindow(state: GreenHealthState): boolean {
  return state.framesLeft > 0 && state.greenAmount > 0;
}

/**
 * Open a green recovery window after a special fires.
 * Replaces any existing window (re-using a second special resets the timer).
 * @param hpCost Amount of HP spent — this becomes the recoverable portion.
 */
export function beginGreenWindow(state: GreenHealthState, hpCost: number): void {
  state.greenAmount = hpCost;
  state.framesLeft = GREEN_RECOVERY_WINDOW_FRAMES;
  state.hitsLanded = 0;
}

/**
 * Advance the recovery timer by one frame.
 * When the window expires naturally, green HP becomes permanent loss — the
 * state resets to zero automatically.
 */
export function tickGreenHealth(state: GreenHealthState): void {
  if (state.framesLeft <= 0) return;
  state.framesLeft -= 1;
  if (state.framesLeft === 0) {
    state.greenAmount = 0;
    state.hitsLanded = 0;
  }
}

/**
 * Notify that the hero landed a hit on an enemy.
 * @returns The HP amount to restore once the threshold is reached; 0 otherwise.
 */
export function notifyHitLanded(state: GreenHealthState): number {
  if (!hasActiveGreenWindow(state)) return 0;
  state.hitsLanded += 1;
  if (state.hitsLanded >= GREEN_RECOVERY_HIT_THRESHOLD) {
    const restore = state.greenAmount;
    state.greenAmount = 0;
    state.framesLeft = 0;
    state.hitsLanded = 0;
    return restore;
  }
  return 0;
}

/**
 * Lock in green HP as permanent loss because the hero took a hit.
 * Call this whenever the player receives enemy damage during an active window.
 */
export function lockInGreenLoss(state: GreenHealthState): void {
  state.greenAmount = 0;
  state.framesLeft = 0;
  state.hitsLanded = 0;
}
